import React, { useState, useEffect } from "react";
import { css } from "@emotion/react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "gatsby";
import { fonts, colors, weights, mediaQueries } from "../styles";

const styles = {
  overlay: css`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1090;
    display: flex;
    align-items: center;
    justify-content: center;
  `,
  banner: css`
    background-color: ${colors.white};
    color: ${colors.lbGray};
    padding: 1.5rem;
    border-radius: 12px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
    max-width: 90%;
    width: 400px;
    font-family: ${fonts.sans};
    font-size: 0.9rem;
    z-index: 5100;
  `,
  content: css`
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    text-align: center;
    ${mediaQueries.phoneLarge} {
      text-align: center;
    }
  `,
  text: css`
    font-size: 0.85rem;
    font-weight: ${weights.light};
    a {
      color: ${colors.brBlue3};
      text-decoration: underline;
      text-underline-offset: 2px;
      &:hover,
      &:focus {
        color: ${colors.brBlue2};
      }
    }
    margin-bottom: 0;
  `,
  buttonContainer: css`
    display: flex;
    justify-content: center;
    gap: 1rem;
    ${mediaQueries.phoneLarge} {
      justify-content: center;
    }
  `,
  buttonBase: css`
    font-family: ${fonts.sans};
    font-size: 0.8rem;
    padding: 0.45rem 2rem;
    border-radius: 5px;
    background-color: ${colors.brBlue3};
    color: ${colors.white};
    border: none;
    font-weight: ${weights.medium};
    cursor: pointer;
    transition: background-color 0.2s ease;
    &:hover,
    &:focus {
      background-color: ${colors.brBlue2};
    }
  `,
};

const ConsentBanner = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Set default consent state immediately
    if (typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];
      window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
      window.gtag("consent", "default", {
        ad_user_data: "denied",
        ad_personalization: "denied",
        ad_storage: "denied",
        analytics_storage: "granted",
        functionality_storage: "granted",
        wait_for_update: 2000,
      });
      window.gtag("js", new Date());
    }

    // Check for prior consent
    const consent = localStorage.getItem("cookieConsent");
    if (consent !== "accepted") {
      setIsVisible(true);
    } else {
      // Apply saved consent for returning users
      const savedPreferences = JSON.parse(localStorage.getItem("consentPreferences") || "{}");
      if (typeof window !== "undefined" && window.gtag) {
        window.gtag("consent", "update", {
          ad_user_data: "denied",
          ad_personalization: "denied",
          ad_storage: "denied",
          analytics_storage: "granted",
          functionality_storage: "granted",
        });
      }
    }
  }, []);

  const handleAcceptAll = () => {
    const preferences = { analytics: true, ads: false };
    localStorage.setItem("cookieConsent", "accepted");
    localStorage.setItem("consentPreferences", JSON.stringify(preferences));
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("consent", "update", {
        ad_user_data: "denied",
        ad_personalization: "denied",
        ad_storage: "denied",
        analytics_storage: "granted",
        functionality_storage: "granted",
      });
      window.gtag("event", "consent_accept_all", { event_category: "Consent" });
    }
    setIsVisible(false);
  };

  const MotionDiv =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: no-preference)").matches
      ? motion.div
      : "div";

  return (
    <AnimatePresence>
      {isVisible && (
        <MotionDiv
          css={styles.overlay}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <MotionDiv
            css={styles.banner}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            role="dialog"
            aria-modal="true"
            aria-label="Cookie consent banner"
          >
            <div css={styles.content}>
              <p css={styles.text}>
                We use cookies to enhance your experience. By continuing to browse, you agree to saving LB and third-party cookies on your device. See our{" "}
                <Link to="/privacy-policy">privacy policy</Link>.
              </p>
              <div css={styles.buttonContainer}>
                <button css={styles.buttonBase} onClick={handleAcceptAll}>
                  Dismiss
                </button>
              </div>
            </div>
          </MotionDiv>
        </MotionDiv>
      )}
    </AnimatePresence>
  );
};

export default ConsentBanner;